import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail } from 'lucide-react';
import api from '@/lib/api';
import useSEO from '@/hooks/useSEO';

const Login = () => {
  useSEO({ title: 'Sign in — EpisodeKit', path: '/login', index: false });
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Invalid credentials';
      if (status === 403 && detail.toLowerCase().includes('verify')) {
        setUnverifiedEmail(formData.email);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend. Try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="login-page">
      <div className="noise-overlay" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-8">
            <img 
              src="/logo-full.png" 
              alt="EpisodeKit" 
              className="h-10 w-auto"
            />
          </Link>
          <h1 className="text-3xl font-unbounded font-bold mb-2">Welcome Back</h1>
          <p className="text-neutral-400">Sign in to continue to your dashboard</p>
        </div>

        <div className="card-base">
          {unverifiedEmail && (
            <div className="mb-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="unverified-banner">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-amber-300 font-medium">Email not verified</p>
                  <p className="text-xs text-neutral-400 mt-1">Please check your inbox for the verification link.</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-amber-400 hover:text-amber-300 p-0 h-auto text-xs"
                    onClick={handleResendVerification}
                    disabled={resending}
                    data-testid="resend-verification-btn"
                  >
                    {resending ? 'Sending...' : 'Resend verification email'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-base w-full"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-base w-full pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <div className="spinner w-5 h-5" /> : 'Sign In'}
            </Button>
          </form>
        </div>

        <p className="text-center mt-6 text-neutral-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
